// "Talking" tab in the Characters panel: characters whose animation has a talking mouth
// ("Mouth_Talk"), so they move their mouth while their voice plays.
//
// The server does not say which characters have one, so each character's file is checked once
// (a few at a time, in the background) and the answer is remembered in this browser. Characters
// served by this app (Utils/localCharacters.js) are known to talk and are shown straight away.

import { assetUrl } from "./assetUrl";

const CACHE_KEY = "toons_talking_characters_v1";
const AT_ONCE = 6;

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
}

function writeCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    /* storage full or blocked: it is checked again next time */
  }
}

/** True when the animation file has a talking mouth. */
export async function fileTalks(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error("HTTP " + resp.status);
  const text = await resp.text();
  return text.indexOf('"Mouth_Talk"') !== -1;
}

/**
 * Finds the talking characters among the panel's character rows (rows as the server sends them:
 * { Id, DefaultPath, PreviewPath, GesturePaths, ... }).
 * onUpdate(talkingRows, { checked, total }) is called as results come in.
 * Returns a function that stops the background check (when the tab closes).
 */
export function findTalkingCharacters(rows, apiBaseURL, onUpdate) {
  let stopped = false;
  const cache = readCache();
  const talking = new Set();
  const unknown = [];

  for (const row of rows) {
    if (!row || !row.DefaultPath) continue;
    if (row.talks === true) talking.add(row);
    else if (cache[row.DefaultPath] === true) talking.add(row);
    else if (cache[row.DefaultPath] !== false) unknown.push(row);
  }

  const total = unknown.length;
  let checked = 0;
  const report = () => {
    if (!stopped) onUpdate(rows.filter((r) => talking.has(r)), { checked, total });
  };
  report();

  let next = 0;
  const worker = async () => {
    while (!stopped && next < unknown.length) {
      const row = unknown[next++];
      try {
        const yes = await fileTalks(assetUrl(apiBaseURL, row.DefaultPath));
        cache[row.DefaultPath] = yes;
        if (yes) talking.add(row);
      } catch (e) {
        /* not reachable now: not remembered, so it is checked again next time */
      }
      checked++;
      if (checked % 5 === 0 || checked === total) writeCache(cache);
      report();
    }
  };
  for (let i = 0; i < Math.min(AT_ONCE, unknown.length); i++) worker();

  return () => {
    stopped = true;
    writeCache(cache);
  };
}
